import { useState } from "react";
import type { DocumentItem } from "@/types/ui-models";
import { dash, fmtDate } from "../lib/docUtils";
import DocumentTypeBadge from "./DocumentTypeBadge";
import DocumentStatusBadge from "./DocumentStatusBadge";

interface DocumentsTableProps {
  documents: DocumentItem[];
  onView: (doc: DocumentItem) => void;
}

export default function DocumentsTable({ documents, onView }: DocumentsTableProps) {
  const [sortField, setSortField] = useState<keyof DocumentItem>("issueDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = (field: keyof DocumentItem) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...documents].sort((a, b) => {
    const aVal = a[sortField] ?? "";
    const bVal = b[sortField] ?? "";
    const cmp = String(aVal).localeCompare(String(bVal), "pt-BR");
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-background-200 text-left">
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Tipo</th>
            <th
              className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap cursor-pointer hover:text-foreground-700"
              onClick={() => handleSort("title")}
            >
              Título {sortField === "title" && (sortDir === "asc" ? "↑" : "↓")}
            </th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Nº/Código</th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Vínculo</th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Projeto</th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Órgão emissor</th>
            <th
              className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap cursor-pointer hover:text-foreground-700"
              onClick={() => handleSort("issueDate")}
            >
              Emissão {sortField === "issueDate" && (sortDir === "asc" ? "↑" : "↓")}
            </th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Validade</th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Status</th>
            <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Ações</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((doc) => (
            <tr key={doc.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
              <td className="py-2.5 px-3"><DocumentTypeBadge type={doc.type} /></td>
              <td className="py-2.5 px-3">
                <button onClick={() => onView(doc)} className="text-left font-medium text-foreground-800 hover:text-accent-600 transition-colors max-w-[220px] truncate block">
                  {doc.title}
                </button>
              </td>
              <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap font-mono">{dash(doc.number)}</td>
              <td className="py-2.5 px-3 text-xs text-foreground-600 max-w-[140px] truncate">{dash(doc.primaryBinding)}</td>
              <td className="py-2.5 px-3 text-xs text-foreground-500 max-w-[140px] truncate">{dash(doc.projectName)}</td>
              <td className="py-2.5 px-3 text-xs text-foreground-600 max-w-[140px] truncate">{dash(doc.issuingBody)}</td>
              <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap">{fmtDate(doc.issueDate)}</td>
              <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap">{fmtDate(doc.expiryDate)}</td>
              <td className="py-2.5 px-3"><DocumentStatusBadge status={doc.status} /></td>
              <td className="py-2.5 px-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => onView(doc)} className="w-7 h-7 rounded-md hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-accent-600 transition-colors" title="Visualizar">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}